/**
 * Viewer360 Main Component
 * Combines ImageLoader, RotationManager, and VariantManager into a complete 360 viewer
 */

import type { ViewerConfig, VariantConfig } from '../types';
import { ImageLoader } from './ImageLoader';
import { RotationManager } from './RotationManager';
import { VariantManager } from './VariantManager';
import { ZoomManager } from './ZoomManager';
import { GalleryMode, SlideDirection } from './GalleryMode';

export class Viewer360 {
  private config: ViewerConfig;
  private container: HTMLElement;
  private imageLoader: ImageLoader;
  private rotationManager: RotationManager | null = null;
  private galleryMode: GalleryMode | null = null;
  private variantManager: VariantManager;
  private zoomManager: ZoomManager;
  private isGalleryMode: boolean = false;

  // DOM Elements
  private viewerElement!: HTMLElement;
  private canvasContainer!: HTMLElement;
  private currentCanvas!: HTMLCanvasElement;
  private nextCanvas!: HTMLCanvasElement;
  private loadingOverlay!: HTMLElement;
  private loadingBar!: SVGCircleElement;
  private loadingText!: HTMLElement;
  private variantSelector!: HTMLElement;
  private frameIndicator!: HTMLElement;
  private frameLoadingSpinner!: HTMLElement;

  private isReady: boolean = false;
  private currentCtx!: CanvasRenderingContext2D;
  private nextCtx!: CanvasRenderingContext2D;
  private lastFrame: number = 0; // For tracking drag direction
  private isWaitingForFrame: boolean = false; // Block rotation while loading a frame

  constructor(config: ViewerConfig) {
    // Find initial variant to get its specific settings
    const initialVariant = config.initialVariant
      ? config.variants.find(v => v.id === config.initialVariant)
      : config.variants[0];

    // Check if initial variant is gallery mode
    this.isGalleryMode = initialVariant?.galleryMode ?? false;

    // Get wrapAround from initial variant (each variant can have different wrapAround)
    const wrapAround = initialVariant?.wrapAround ?? (config.wrapAround !== false);
    const frameCount = initialVariant?.frameCount ?? config.variants[0]?.frameCount ?? 8;

    this.config = {
      sensitivity: 10,
      enableTouch: true,
      enableMouse: true,
      preloadFrames: 4,
      initialFrame: 0,
      autoRotate: false,
      autoRotateSpeed: 150,
      wrapAround,
      ...config,
    };

    this.container = config.container;

    // Calculate initial frame: center for limited mode, 0 for 360° (gallery mode always starts at 0)
    const initialFrame = this.isGalleryMode
      ? 0
      : (wrapAround
        ? (config.initialFrame ?? 0)
        : (config.initialFrame ?? Math.floor(frameCount / 2)));

    // Initialize modules
    this.imageLoader = new ImageLoader();

    // Initialize rotation or gallery mode based on variant
    if (this.isGalleryMode) {
      this.galleryMode = new GalleryMode({
        frameCount: frameCount,
        onFrameChange: (frame, direction) => this.onGalleryFrameChange(frame, direction),
        onSwipeStart: () => this.onDragStart(),
        onSwipeEnd: () => this.onDragEnd(),
      });
    } else {
      this.rotationManager = new RotationManager({
        frameCount: frameCount,
        sensitivity: this.config.sensitivity!,
        enableMomentum: true,
        momentumDecay: 0.95,
        wrapAround: wrapAround,
        onFrameChange: (frame) => this.onFrameChange(frame),
        onDragStart: () => this.onDragStart(),
        onDragEnd: () => this.onDragEnd(),
      });
    }

    this.variantManager = new VariantManager({
      variants: config.variants,
      initialVariant: config.initialVariant,
      transitionDuration: 300,
      onVariantChange: (variant, prev) => this.onVariantChange(variant, prev),
      onTransitionStart: () => this.onTransitionStart(),
      onTransitionEnd: () => this.onTransitionEnd(),
    });

    this.zoomManager = new ZoomManager({
      minScale: 1,
      maxScale: config.maxZoom ?? 3,
      zoomStep: 0.25,
      doubleTapScale: 2,
      animationDuration: 200,
      onZoomChange: () => this.onZoomChange(),
      onModeChange: (mode) => this.onModeChange(mode),
    });

    // Build UI
    this.buildUI();

    // Set initial frame (after all managers are initialized)
    if (this.isGalleryMode) {
      this.galleryMode!.setFrame(initialFrame);
    } else {
      this.rotationManager!.setFrame(initialFrame);
    }

    // Initialize
    this.initialize();
  }

  /**
   * Build the viewer UI
   */
  private buildUI(): void {
    // Clear container
    this.container.innerHTML = '';

    // Main viewer element
    this.viewerElement = document.createElement('div');
    this.viewerElement.className = this.isGalleryMode ? 'viewer-360 gallery-mode' : 'viewer-360';

    // Canvas container for image display
    this.canvasContainer = document.createElement('div');
    this.canvasContainer.className = 'viewer-canvas-container';

    // Current canvas (visible)
    this.currentCanvas = document.createElement('canvas');
    this.currentCanvas.className = 'viewer-canvas viewer-canvas-current';
    this.currentCtx = this.currentCanvas.getContext('2d')!;

    // Next canvas (for crossfade)
    this.nextCanvas = document.createElement('canvas');
    this.nextCanvas.className = 'viewer-canvas viewer-canvas-next';
    this.nextCtx = this.nextCanvas.getContext('2d')!;

    this.canvasContainer.appendChild(this.currentCanvas);
    this.canvasContainer.appendChild(this.nextCanvas);

    // Loading overlay
    this.loadingOverlay = document.createElement('div');
    this.loadingOverlay.className = 'viewer-loading';
    this.loadingOverlay.innerHTML = `
      <div class="viewer-loading-content">
        <div class="viewer-loading-ring">
          <svg viewBox="0 0 100 100">
            <circle class="ring-bg" cx="50" cy="50" r="42"/>
            <circle class="ring-progress" cx="50" cy="50" r="42"/>
          </svg>
          <div class="ring-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              <path d="M9 12l2 2 4-4"/>
            </svg>
          </div>
          <div class="ring-360">${this.isGalleryMode ? '' : '360°'}</div>
        </div>
        <div class="viewer-loading-info">
          <div class="viewer-loading-text">${this.config.loadingText || 'Yükleniyor'}</div>
          <div class="viewer-loading-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
        <div class="viewer-loading-percent">0%</div>
      </div>
    `;
    this.loadingBar = this.loadingOverlay.querySelector('.ring-progress')!;
    this.loadingText = this.loadingOverlay.querySelector('.viewer-loading-text')!;

    // Variant selector
    this.variantSelector = document.createElement('div');
    this.variantSelector.className = 'viewer-variants';
    this.buildVariantSelector();

    // Frame indicator
    this.frameIndicator = document.createElement('div');
    this.frameIndicator.className = 'viewer-frame-indicator';

    // Frame loading spinner (shown when waiting for a specific frame)
    this.frameLoadingSpinner = document.createElement('div');
    this.frameLoadingSpinner.className = 'viewer-frame-spinner';
    this.frameLoadingSpinner.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
        <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
      </svg>
    `;

    // Assemble base elements
    this.viewerElement.appendChild(this.canvasContainer);
    this.viewerElement.appendChild(this.loadingOverlay);
    this.viewerElement.appendChild(this.frameLoadingSpinner);
    this.viewerElement.appendChild(this.variantSelector);
    this.viewerElement.appendChild(this.frameIndicator);

    // Add mode-specific UI
    if (this.isGalleryMode) {
      this.buildGalleryUI();
    } else {
      this.build360UI();
    }

    this.container.appendChild(this.viewerElement);
  }

  /**
   * Build 360° rotation mode UI (drag hint)
   */
  private build360UI(): void {
    const dragHint = document.createElement('div');
    dragHint.className = 'viewer-drag-hint';
    const hintText = this.config.dragHintText || 'Döndürmek için sürükle';
    dragHint.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 4l6 6-6 6M10 4l-6 6 6 6"/>
      </svg>
      <span>${hintText}</span>
    `;
    this.viewerElement.appendChild(dragHint);
  }

  /**
   * Build gallery mode UI (navigation arrows, thumbnails)
   */
  private buildGalleryUI(): void {
    const variant = this.variantManager.getCurrentVariant();
    if (!variant) return;

    // Navigation arrows
    const prevBtn = document.createElement('button');
    prevBtn.className = 'gallery-nav-btn gallery-nav-prev';
    prevBtn.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M15 18l-6-6 6-6"/>
      </svg>
    `;
    prevBtn.addEventListener('click', () => this.galleryPrev());

    const nextBtn = document.createElement('button');
    nextBtn.className = 'gallery-nav-btn gallery-nav-next';
    nextBtn.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M9 18l6-6-6-6"/>
      </svg>
    `;
    nextBtn.addEventListener('click', () => this.galleryNext());

    // Thumbnail bar
    const thumbnailBar = document.createElement('div');
    thumbnailBar.className = 'gallery-thumbnails';

    // Create thumbnails
    for (let i = 0; i < variant.frameCount; i++) {
      const thumb = document.createElement('button');
      thumb.className = 'gallery-thumb' + (i === 0 ? ' active' : '');
      thumb.dataset.frame = String(i);

      // Create thumbnail image
      const img = document.createElement('img');
      const frameNumber = i + 1;
      const fileName = variant.filePattern.replace('{n}', String(frameNumber));
      const originalUrl = `${variant.imagePath}/${fileName}`;
      img.alt = `Fotoğraf ${frameNumber}`;
      img.loading = 'lazy';

      this.imageLoader.loadImageUrl(originalUrl).then(src => {
        img.src = src;
      });

      thumb.appendChild(img);
      thumb.addEventListener('click', () => this.galleryGoToFrame(i));
      thumbnailBar.appendChild(thumb);
    }

    this.viewerElement.appendChild(prevBtn);
    this.viewerElement.appendChild(nextBtn);
    this.viewerElement.appendChild(thumbnailBar);

    // Update navigation state
    this.updateGalleryNavigation();
  }

  /**
   * Update gallery navigation buttons and thumbnails
   */
  private updateGalleryNavigation(): void {
    if (!this.isGalleryMode || !this.galleryMode) return;

    const currentFrame = this.galleryMode.getFrame();
    const variant = this.variantManager.getCurrentVariant();
    if (!variant) return;

    // Update prev/next buttons
    const prevBtn = this.viewerElement.querySelector('.gallery-nav-prev') as HTMLButtonElement;
    const nextBtn = this.viewerElement.querySelector('.gallery-nav-next') as HTMLButtonElement;
    if (prevBtn) prevBtn.disabled = !this.galleryMode.canGoPrev();
    if (nextBtn) nextBtn.disabled = !this.galleryMode.canGoNext();

    // Update active thumbnail
    const thumbs = this.viewerElement.querySelectorAll('.gallery-thumb');
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === currentFrame);
    });

    // Scroll active thumbnail into view (only if outside visible area)
    const thumbnailBar = this.viewerElement.querySelector('.gallery-thumbnails') as HTMLElement;
    const activeThumb = this.viewerElement.querySelector('.gallery-thumb.active') as HTMLElement;
    if (thumbnailBar && activeThumb) {
      const barRect = thumbnailBar.getBoundingClientRect();
      const thumbRect = activeThumb.getBoundingClientRect();

      // Check if thumbnail is outside visible area (with padding)
      const padding = 20;
      const isOutsideLeft = thumbRect.left < barRect.left + padding;
      const isOutsideRight = thumbRect.right > barRect.right - padding;

      if (isOutsideLeft || isOutsideRight) {
        // Calculate scroll to center the thumbnail
        const thumbCenter = thumbRect.left + thumbRect.width / 2;
        const barCenter = barRect.left + barRect.width / 2;
        const scrollOffset = thumbCenter - barCenter;
        thumbnailBar.scrollLeft += scrollOffset;
      }
    }
  }

  /**
   * Gallery navigation methods
   * These reset zoom first to ensure navigation works even when zoomed
   */
  private galleryPrev(): void {
    if (this.galleryMode) {
      // Reset zoom if zoomed, then navigate
      if (this.zoomManager.isZoomed()) {
        this.zoomManager.resetZoom();
      }
      this.galleryMode.goPrev();
    }
  }

  private galleryNext(): void {
    if (this.galleryMode) {
      // Reset zoom if zoomed, then navigate
      if (this.zoomManager.isZoomed()) {
        this.zoomManager.resetZoom();
      }
      this.galleryMode.goNext();
    }
  }

  private galleryGoToFrame(frame: number): void {
    if (this.galleryMode) {
      // Reset zoom if zoomed, then navigate
      if (this.zoomManager.isZoomed()) {
        this.zoomManager.resetZoom();
      }
      this.galleryMode.setFrame(frame);
    }
  }

  /**
   * Build variant selection buttons
   */
  private buildVariantSelector(): void {
    this.variantSelector.innerHTML = '';

    const variants = this.variantManager.getAllVariants();
    const currentVariant = this.variantManager.getCurrentVariant();

    for (const variant of variants) {
      const button = document.createElement('button');
      button.className = 'viewer-variant-btn';
      button.dataset.variant = variant.id;
      button.title = variant.name;

      if (variant.id === currentVariant?.id) {
        button.classList.add('active');
      }

      // Color swatch
      const swatch = document.createElement('span');
      swatch.className = 'viewer-variant-swatch';
      swatch.style.backgroundColor = variant.color;

      // Name label
      const label = document.createElement('span');
      label.className = 'viewer-variant-label';
      label.textContent = variant.name;

      button.appendChild(swatch);
      button.appendChild(label);

      button.addEventListener('click', () => {
        this.switchVariant(variant.id);
      });

      this.variantSelector.appendChild(button);
    }
  }

  /**
   * Update variant selector active state
   */
  private updateVariantSelector(): void {
    const currentVariant = this.variantManager.getCurrentVariant();
    const buttons = this.variantSelector.querySelectorAll('.viewer-variant-btn');

    buttons.forEach((btn) => {
      const button = btn as HTMLButtonElement;
      if (button.dataset.variant === currentVariant?.id) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
  }

  /**
   * Initialize the viewer
   */
  private async initialize(): Promise<void> {
    const variant = this.variantManager.getCurrentVariant();
    if (!variant) {
      return;
    }

    // Set canvas size
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());

    // Show loading
    this.showLoading();

    // Get the current frame from appropriate manager
    const currentFrame = this.isGalleryMode
      ? this.galleryMode!.getFrame()
      : this.rotationManager!.getFrame();

    // Preload initial frames
    await this.imageLoader.preloadInitialFrames(
      variant,
      currentFrame,
      this.config.preloadFrames ?? 4,
      (loaded, total) => {
        this.updateLoadingProgress(loaded / total, 'Loading preview...');
      }
    );

    // Render initial frame
    await this.renderFrame(currentFrame);

    // Hide loading for initial view
    this.hideLoading();

    // Attach appropriate controls based on mode
    if (this.isGalleryMode) {
      this.galleryMode!.attach(this.canvasContainer);
    } else {
      this.rotationManager!.attach(this.canvasContainer);
    }

    // Attach zoom controls
    this.zoomManager.attach(this.canvasContainer);

    // Load remaining frames in background
    this.loadRemainingFrames(variant);

    // Start auto-rotate if enabled (only for 360° mode)
    if (this.config.autoRotate && !this.isGalleryMode) {
      this.rotationManager!.startAutoRotate(this.config.autoRotateSpeed);
    }

    this.isReady = true;
    this.config.onReady?.();
  }

  /**
   * Load remaining frames in background using spiral pattern
   * Loads frames closest to current position first
   */
  private async loadRemainingFrames(variant: VariantConfig): Promise<void> {
    const currentFrame = this.isGalleryMode
      ? this.galleryMode!.getFrame()
      : this.rotationManager!.getFrame();

    // Use spiral loading - loads frames radiating from current position
    await this.imageLoader.loadInSpiralPattern(
      variant,
      currentFrame,
      (state) => {
        const progress = state.loaded / state.total;
        this.config.onLoadProgress?.(progress, variant.id);
        this.updateFrameIndicator();
      }
    );
  }

  /**
   * Resize canvas to fit container
   */
  private resizeCanvas(): void {
    const rect = this.canvasContainer.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    // Use a reasonable max size for performance
    const maxSize = 1200;
    const width = Math.min(rect.width, maxSize);
    const height = Math.min(rect.height, maxSize);

    for (const canvas of [this.currentCanvas, this.nextCanvas]) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      const ctx = canvas.getContext('2d')!;
      ctx.scale(dpr, dpr);
    }

    // Reset zoom on resize
    if (this.isReady) {
      this.zoomManager.resetZoom();
      const currentFrame = this.isGalleryMode
        ? this.galleryMode!.getFrame()
        : this.rotationManager!.getFrame();
      this.renderFrame(currentFrame);
    }
  }

  /**
   * Render a specific frame - shows loading spinner if frame not available
   * Never jumps to a different frame, waits for the correct frame instead
   */
  private async renderFrame(frameIndex: number, canvas?: HTMLCanvasElement): Promise<void> {
    const variant = this.variantManager.getCurrentVariant();
    if (!variant) return;

    const targetCanvas = canvas ?? this.currentCanvas;
    const ctx = targetCanvas === this.currentCanvas ? this.currentCtx : this.nextCtx;

    // Try to get cached frame first (instant)
    const img = this.imageLoader.getCachedFrame(variant.id, frameIndex);

    if (img) {
      // Frame is loaded - render it and hide spinner
      this.drawImageToCanvas(ctx, img, targetCanvas);
      this.hideFrameSpinner();
    } else {
      // Frame not loaded - show spinner and BLOCK all rotation until loaded
      this.isWaitingForFrame = true;
      this.showFrameSpinner();
      if (this.rotationManager) {
        this.rotationManager.stopMomentum();
      }

      // Load this frame
      this.imageLoader.getFrame(variant, frameIndex).then((loadedImg) => {
        if (loadedImg) {
          this.drawImageToCanvas(ctx, loadedImg, targetCanvas);
        }
        // Unblock rotation
        this.isWaitingForFrame = false;
        this.hideFrameSpinner();
        this.updateFrameIndicator();
      });
    }

    this.updateFrameIndicator();
  }

  /**
   * Show the frame loading spinner
   */
  private showFrameSpinner(): void {
    this.frameLoadingSpinner.classList.add('visible');
  }

  /**
   * Hide the frame loading spinner
   */
  private hideFrameSpinner(): void {
    this.frameLoadingSpinner.classList.remove('visible');
  }

  /**
   * Draw image to canvas with proper scaling and zoom
   */
  private drawImageToCanvas(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    canvas: HTMLCanvasElement
  ): void {
    const dpr = window.devicePixelRatio || 1;
    const canvasWidth = canvas.width / dpr;
    const canvasHeight = canvas.height / dpr;

    // Get zoom state
    const scale = this.zoomManager.getScale();
    const offset = this.zoomManager.getOffset();

    // Calculate scaling to fit (contain)
    const imgRatio = img.width / img.height;
    const canvasRatio = canvasWidth / canvasHeight;

    let drawWidth: number;
    let drawHeight: number;

    if (imgRatio > canvasRatio) {
      // Image is wider
      drawWidth = canvasWidth;
      drawHeight = canvasWidth / imgRatio;
    } else {
      // Image is taller
      drawHeight = canvasHeight;
      drawWidth = canvasHeight * imgRatio;
    }

    // Apply zoom scale
    const scaledWidth = drawWidth * scale;
    const scaledHeight = drawHeight * scale;

    // Calculate position with zoom offset
    const x = (canvasWidth - scaledWidth) / 2 + offset.x;
    const y = (canvasHeight - scaledHeight) / 2 + offset.y;

    // Clear and draw
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
  }

  /**
   * Gallery frame change handler - with slide animation
   */
  private async onGalleryFrameChange(frame: number, direction: SlideDirection): Promise<void> {
    const variant = this.variantManager.getCurrentVariant();
    if (!variant) return;

    // Disable gallery input during animation to prevent state desync
    this.galleryMode?.setEnabled(false);

    // Get the image for the new frame
    const img = this.imageLoader.getCachedFrame(variant.id, frame);

    if (img) {
      // Frame is cached - perform slide animation
      await this.performGallerySlideAnimation(frame, direction, img);
    } else {
      // Frame not loaded - show spinner and wait
      this.showFrameSpinner();
      const loadedImg = await this.imageLoader.getFrame(variant, frame);
      this.hideFrameSpinner();

      if (loadedImg) {
        await this.performGallerySlideAnimation(frame, direction, loadedImg);
      }
    }

    // Re-enable gallery input after animation
    this.galleryMode?.setEnabled(true);

    // Update gallery navigation
    this.updateGalleryNavigation();
    this.updateFrameIndicator();

    this.config.onFrameChange?.(frame);
  }

  /**
   * Perform the slide animation for gallery mode
   */
  private async performGallerySlideAnimation(
    _frame: number,
    direction: SlideDirection,
    newImg: HTMLImageElement
  ): Promise<void> {
    return new Promise((resolve) => {
      // Render new image to next canvas
      this.drawImageToCanvas(this.nextCtx, newImg, this.nextCanvas);

      // Set up animation classes based on direction
      // Next = swipe left = current goes left, new comes from right
      // Prev = swipe right = current goes right, new comes from left
      const slideOutClass = direction === 'next' ? 'slide-out-left' : 'slide-out-right';
      const slideInClass = direction === 'next' ? 'slide-in-right' : 'slide-in-left';

      // Force a reflow to ensure animations start fresh
      void this.currentCanvas.offsetWidth;
      void this.nextCanvas.offsetWidth;

      // Start both animations simultaneously
      this.currentCanvas.classList.add(slideOutClass);
      this.nextCanvas.classList.add(slideInClass);

      // Animation duration (match CSS)
      const animationDuration = 380;

      setTimeout(() => {
        // Copy next canvas content to current canvas
        const dpr = window.devicePixelRatio || 1;
        this.currentCtx.clearRect(0, 0, this.currentCanvas.width / dpr, this.currentCanvas.height / dpr);
        this.currentCtx.drawImage(
          this.nextCanvas,
          0, 0,
          this.currentCanvas.width / dpr,
          this.currentCanvas.height / dpr
        );

        // Reset both canvases
        this.currentCanvas.classList.remove(slideOutClass);
        this.nextCanvas.classList.remove(slideInClass);

        // Reset transforms explicitly
        this.currentCanvas.style.transform = '';
        this.nextCanvas.style.transform = '';

        resolve();
      }, animationDuration);
    });
  }

  /**
   * Frame change handler - with predictive loading
   */
  private onFrameChange(frame: number): void {
    // Block frame changes while waiting for a frame to load
    // This prevents jumping around during loading
    if (this.isWaitingForFrame) {
      return;
    }

    const variant = this.variantManager.getCurrentVariant();

    // Determine drag direction and trigger predictive loading (only for 360° mode)
    if (variant && this.lastFrame !== frame && !this.isGalleryMode) {
      const frameCount = variant.frameCount;
      // Calculate direction (accounting for wrap-around)
      let diff = frame - this.lastFrame;
      if (Math.abs(diff) > frameCount / 2) {
        diff = diff > 0 ? diff - frameCount : diff + frameCount;
      }
      const direction: 1 | -1 = diff > 0 ? 1 : -1;

      // Trigger predictive loading in drag direction
      this.imageLoader.predictiveLoad(variant, frame, direction, 6);
    }

    this.lastFrame = frame;
    this.renderFrame(frame);

    // Update gallery navigation if in gallery mode
    if (this.isGalleryMode) {
      this.updateGalleryNavigation();
    }

    this.config.onFrameChange?.(frame);
  }

  /**
   * Drag start handler
   */
  private onDragStart(): void {
    this.viewerElement.classList.add('is-dragging');
    // Hide drag hint on first interaction
    this.viewerElement.classList.add('hide-hint');
  }

  /**
   * Drag end handler
   */
  private onDragEnd(): void {
    this.viewerElement.classList.remove('is-dragging');
  }

  /**
   * Zoom change handler
   */
  private onZoomChange(): void {
    const currentFrame = this.isGalleryMode
      ? this.galleryMode!.getFrame()
      : this.rotationManager!.getFrame();
    this.renderFrame(currentFrame);

    // In gallery mode, also update nextCanvas to prevent ghost image
    // because nextCanvas has opacity: 1 !important in gallery mode CSS
    if (this.isGalleryMode) {
      this.renderFrame(currentFrame, this.nextCanvas);
    }
  }

  /**
   * Mode change handler (rotate vs pan)
   */
  private onModeChange(mode: 'rotate' | 'pan'): void {
    if (this.isGalleryMode) {
      this.galleryMode?.setEnabled(mode === 'rotate');
    } else {
      this.rotationManager?.setEnabled(mode === 'rotate');
    }
  }

  /**
   * Variant change handler
   */
  private async onVariantChange(variant: VariantConfig, prev: VariantConfig | null): Promise<void> {
    // Check if color changed (photos are color-based, not combination-based)
    const colorChanged = prev?.colorId !== variant.colorId;

    // If only custom changed (same color), just update UI and notify
    if (!colorChanged && prev) {
      this.updateVariantSelector();
      this.config.onVariantChange?.(variant.id);
      return;
    }

    // Color changed - do full variant switch
    // Reset zoom on color change
    this.zoomManager.resetZoom();

    // Check if mode changed
    const wasGalleryMode = prev?.galleryMode ?? false;
    const isNowGalleryMode = variant.galleryMode ?? false;
    const modeChanged = wasGalleryMode !== isNowGalleryMode;

    // Clear nextCanvas to prevent ghost images
    const dpr = window.devicePixelRatio || 1;
    this.nextCtx.clearRect(0, 0, this.nextCanvas.width / dpr, this.nextCanvas.height / dpr);
    this.nextCanvas.style.opacity = '0';

    // Switch mode if needed
    if (modeChanged) {
      // Disable CSS transitions temporarily to prevent diagonal slide animation
      this.currentCanvas.style.transition = 'none';
      this.nextCanvas.style.transition = 'none';

      this.switchMode(isNowGalleryMode, variant.frameCount, variant.wrapAround);
      this.rebuildModeUI();

      // Force reflow to apply styles immediately
      void this.currentCanvas.offsetWidth;

      // Re-enable transitions after a frame
      requestAnimationFrame(() => {
        this.currentCanvas.style.transition = '';
        this.nextCanvas.style.transition = '';
      });
    } else {
      // Update existing manager config
      if (this.isGalleryMode) {
        this.galleryMode?.updateConfig({
          frameCount: variant.frameCount
        });
        this.galleryMode?.setFrame(0);
        this.rebuildModeUI();
      } else {
        this.rotationManager?.updateConfig({
          frameCount: variant.frameCount,
          wrapAround: variant.wrapAround
        });
      }
    }

    // Calculate and set appropriate initial frame for the new variant
    if (this.isGalleryMode) {
      // Gallery mode always starts at frame 0
      this.galleryMode?.setFrame(0);
    } else {
      // For 360° mode: center frame for limited (wrapAround=false), 0 for infinite
      const targetFrame = variant.wrapAround
        ? 0
        : Math.floor(variant.frameCount / 2);
      this.rotationManager?.setFrame(targetFrame);
    }

    // Update UI
    this.updateVariantSelector();

    // Get current frame from appropriate manager
    const currentFrame = this.isGalleryMode
      ? this.galleryMode!.getFrame()
      : this.rotationManager!.getFrame();

    // Show loading if frames not cached
    if (!this.imageLoader.isFrameLoaded(variant.id, currentFrame)) {
      this.showLoading();

      // Load initial frames for new variant
      await this.imageLoader.preloadInitialFrames(
        variant,
        currentFrame,
        this.config.preloadFrames ?? 4,
        (loaded, total) => {
          this.updateLoadingProgress(loaded / total, `Loading ${variant.name}...`);
        }
      );
    }

    // Render directly to current canvas - no animation
    await this.renderFrame(currentFrame);
    this.hideLoading();

    // Load remaining frames in background
    this.loadRemainingFrames(variant);

    // Notify callback
    this.config.onVariantChange?.(variant.id);
  }

  /**
   * Switch between 360° mode and gallery mode
   * Note: Caller should call rebuildModeUI() after this
   */
  private switchMode(toGalleryMode: boolean, frameCount: number, wrapAround: boolean): void {
    // Detach current manager
    if (this.isGalleryMode) {
      this.galleryMode?.detach();
      this.galleryMode = null;
    } else {
      this.rotationManager?.detach();
      this.rotationManager = null;
    }

    // Update mode flag
    this.isGalleryMode = toGalleryMode;

    // Create and attach new manager
    if (toGalleryMode) {
      this.galleryMode = new GalleryMode({
        frameCount: frameCount,
        onFrameChange: (frame, direction) => this.onGalleryFrameChange(frame, direction),
        onSwipeStart: () => this.onDragStart(),
        onSwipeEnd: () => this.onDragEnd(),
      });
      this.galleryMode.attach(this.canvasContainer);
    } else {
      this.rotationManager = new RotationManager({
        frameCount: frameCount,
        sensitivity: this.config.sensitivity!,
        enableMomentum: true,
        momentumDecay: 0.95,
        wrapAround: wrapAround,
        onFrameChange: (frame) => this.onFrameChange(frame),
        onDragStart: () => this.onDragStart(),
        onDragEnd: () => this.onDragEnd(),
      });
      this.rotationManager.attach(this.canvasContainer);
    }
  }

  /**
   * Rebuild mode-specific UI elements
   */
  private rebuildModeUI(): void {
    // Update viewer class
    this.viewerElement.className = this.isGalleryMode ? 'viewer-360 gallery-mode' : 'viewer-360';

    // Remove old mode-specific elements
    const oldDragHint = this.viewerElement.querySelector('.viewer-drag-hint');
    const oldPrevBtn = this.viewerElement.querySelector('.gallery-nav-prev');
    const oldNextBtn = this.viewerElement.querySelector('.gallery-nav-next');
    const oldThumbnails = this.viewerElement.querySelector('.gallery-thumbnails');

    oldDragHint?.remove();
    oldPrevBtn?.remove();
    oldNextBtn?.remove();
    oldThumbnails?.remove();

    // Add new mode-specific elements
    if (this.isGalleryMode) {
      this.buildGalleryUI();
    } else {
      this.build360UI();
    }
  }

  /**
   * Transition start handler
   */
  private onTransitionStart(): void {
    this.viewerElement.classList.add('is-transitioning');
  }

  /**
   * Transition end handler
   */
  private onTransitionEnd(): void {
    this.viewerElement.classList.remove('is-transitioning');
  }

  /**
   * Show loading overlay
   */
  private showLoading(): void {
    this.loadingOverlay.classList.remove('complete');
    this.loadingOverlay.classList.add('visible');

    // Reset progress
    const circumference = 2 * Math.PI * 42;
    this.loadingBar.style.strokeDashoffset = String(circumference);

    const percentEl = this.loadingOverlay.querySelector('.viewer-loading-percent');
    if (percentEl) {
      percentEl.textContent = '0%';
    }
  }

  /**
   * Hide loading overlay with completion animation
   */
  private hideLoading(): void {
    this.loadingOverlay.classList.add('complete');

      this.loadingBar.style.strokeDashoffset = '0';

    const percentEl = this.loadingOverlay.querySelector('.viewer-loading-percent');
    if (percentEl) {
      percentEl.textContent = '100%';
    }

    setTimeout(() => {
      this.loadingOverlay.classList.remove('visible');
    }, 400);
  }

  /**
   * Update loading progress
   */
  private updateLoadingProgress(progress: number, text?: string): void {
    // Update circular progress (stroke-dashoffset)
    const circumference = 2 * Math.PI * 42; // r=42
    const offset = circumference * (1 - progress);
    this.loadingBar.style.strokeDashoffset = String(offset);

    // Update percentage text
    const percentEl = this.loadingOverlay.querySelector('.viewer-loading-percent');
    if (percentEl) {
      percentEl.textContent = `${Math.round(progress * 100)}%`;
    }

    // Update loading text
    if (text) {
      this.loadingText.textContent = text;
    }
  }

  /**
   * Update frame indicator
   */
  private updateFrameIndicator(): void {
    const variant = this.variantManager.getCurrentVariant();
    if (!variant) return;

    const frame = this.isGalleryMode
      ? this.galleryMode!.getFrame()
      : this.rotationManager!.getFrame();
    const state = this.imageLoader.getLoadingState(variant.id);

    let loadedCount = 0;
    for (let i = 0; i < variant.frameCount; i++) {
      if (this.imageLoader.isFrameLoaded(variant.id, i)) {
        loadedCount++;
      }
    }

    this.frameIndicator.textContent = `${frame + 1}/${variant.frameCount}`;
    if (state && loadedCount < variant.frameCount) {
      this.frameIndicator.textContent += ` (${loadedCount} loaded)`;
    }
  }

  // Public API

  /**
   * Switch to a variant by ID (colorId_customId format)
   */
  async switchVariant(variantId: string): Promise<boolean> {
    return this.variantManager.switchVariant(variantId);
  }

  /**
   * Switch color while keeping current custom variant
   */
  async switchColor(colorId: string): Promise<boolean> {
    const currentCustomId = this.variantManager.getCurrentCustomId() || 'default';
    const newVariantId = `${colorId}_${currentCustomId}`;
    return this.variantManager.switchVariant(newVariantId);
  }

  /**
   * Switch custom variant while keeping current color
   */
  async switchCustom(customId: string): Promise<boolean> {
    const currentColorId = this.variantManager.getCurrentColorId() || 'default';
    const newVariantId = `${currentColorId}_${customId}`;
    return this.variantManager.switchVariant(newVariantId);
  }

  /**
   * Get current color ID
   */
  getCurrentColorId(): string | null {
    return this.variantManager.getCurrentColorId();
  }

  /**
   * Get current custom ID
   */
  getCurrentCustomId(): string | null {
    return this.variantManager.getCurrentCustomId();
  }

  /**
   * Go to a specific frame
   */
  goToFrame(frame: number): void {
    if (this.isGalleryMode) {
      this.galleryMode?.setFrame(frame);
    } else {
      this.rotationManager?.setFrame(frame);
    }
  }

  /**
   * Start auto-rotation (only available in 360° mode)
   */
  startAutoRotate(speed?: number): void {
    if (!this.isGalleryMode) {
      this.rotationManager?.startAutoRotate(speed ?? this.config.autoRotateSpeed);
    }
  }

  /**
   * Stop auto-rotation
   */
  stopAutoRotate(): void {
    this.rotationManager?.stopAutoRotate();
  }

  /**
   * Toggle auto-rotation (only available in 360° mode)
   */
  toggleAutoRotate(): boolean {
    if (this.isGalleryMode) return false;
    if (this.rotationManager?.isAutoRotating()) {
      this.stopAutoRotate();
      return false;
    } else {
      this.startAutoRotate();
      return true;
    }
  }

  /**
   * Get current frame
   */
  getCurrentFrame(): number {
    return this.isGalleryMode
      ? this.galleryMode!.getFrame()
      : this.rotationManager!.getFrame();
  }

  /**
   * Get current variant
   */
  getCurrentVariant(): VariantConfig | null {
    return this.variantManager.getCurrentVariant();
  }

  /**
   * Update UI texts without rebuilding the viewer (e.g. after a language change)
   */
  setTexts(texts: { dragHint?: string; loading?: string }): void {
    if (texts.dragHint !== undefined) {
      this.config.dragHintText = texts.dragHint;
      const hint = this.viewerElement?.querySelector('.viewer-drag-hint span');
      if (hint) hint.textContent = texts.dragHint;
    }
    if (texts.loading !== undefined) {
      this.config.loadingText = texts.loading;
      if (this.loadingText) this.loadingText.textContent = texts.loading;
    }
  }

  /**
   * Check if viewer is ready
   */
  isViewerReady(): boolean {
    return this.isReady;
  }

  /**
   * Destroy the viewer
   */
  destroy(): void {
    if (this.isGalleryMode) {
      this.galleryMode?.destroy();
    } else {
      this.rotationManager?.detach();
    }
    this.zoomManager.detach();
    this.imageLoader.clearAllCache();
    window.removeEventListener('resize', () => this.resizeCanvas());
    this.container.innerHTML = '';
    this.isReady = false;
  }

  // Zoom Public API

  /**
   * Zoom to a specific scale
   */
  zoomTo(scale: number): void {
    this.zoomManager.zoomTo(scale);
  }

  /**
   * Zoom in by one step
   */
  zoomIn(): void {
    this.zoomManager.zoomIn();
  }

  /**
   * Zoom out by one step
   */
  zoomOut(): void {
    this.zoomManager.zoomOut();
  }

  /**
   * Reset zoom to 1x
   */
  resetZoom(): void {
    this.zoomManager.resetZoom();
  }

  /**
   * Get current zoom level
   */
  getZoomLevel(): number {
    return this.zoomManager.getScale();
  }

  /**
   * Check if currently zoomed in
   */
  isZoomed(): boolean {
    return this.zoomManager.isZoomed();
  }
}
